import { signOut } from 'firebase/auth';
import { getValidIdToken, refreshIdToken } from './authService';
import { auth } from '../config/firebase';

const BASE_URL = process.env.REACT_APP_API_URL;

async function request(endpoint, options = {}) {
  const token = await getValidIdToken();
  const config = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  const response = await fetch(`${BASE_URL}${endpoint}`, config);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    if (response.status === 401) {
      if (localStorage.getItem('firebase_refresh_token')) {
        await refreshIdToken();
        return request(endpoint, options);
      } else {
        await signOut(auth);
        localStorage.clear();
        window.location.href = '/';
      }
    }
    throw new Error(error.message || 'API Error');
  }

  // Handle 204 No Content responses (e.g., successful DELETE operations)
  if (response.status === 204) {
    return null;
  }

  return response.json();
}

export const api = {
  auth: {
    // Get current user info
    getMe: () => request('/me'),
  },

  chat: {
    // Get chat list
    getMessages: (chatId) => request(`/chats/${chatId}`),

    // Create a new chat with initial message
    createChat: () => request('/chat/start', { method: 'POST' }),

    // Get history of chats
    getChatHistory: () => request(`/chats`),

    // Existing methods
    sendMessage: (chatId, message) =>
      request(`/chat/${chatId}/message`, {
        method: 'POST',
        body: JSON.stringify({ message }),
      }),

    // Rename Chat Title.
    renameChat: (chatId, name) =>
      request(`/chats/${chatId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      }),

    // Delete Chat
    deleteChat: (chatId) => request(`/chats/${chatId}`, { method: 'DELETE' }),
  },

  feedback: {
    // Submit feedback for assistant message
    submitFeedback: async (feedbackData) => {
      const token = await getValidIdToken();
      const formData = new FormData();
      formData.append('title', feedbackData.title);
      formData.append('description', feedbackData.description);
      formData.append('category', feedbackData.category);
      if (feedbackData.session_id) {
        formData.append('session_id', feedbackData.session_id);
      }

      const response = await fetch(`${BASE_URL}/feedback`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to submit feedback');
      }
      if (response.status === 204) return null;
      return response.json();
    },

    // Send report via email
    sendReport: async (data) => {
      const token = await getValidIdToken();
      const formData = new FormData();
      formData.append('subject', data.subject);
      formData.append('body', data.body);

      const response = await fetch(`${BASE_URL}/send-report`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to send report');
      }

      return response.json();
    },
  },

  admin: {
    // Get all users
    getUsers: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      if (params.email) queryParams.append('email', params.email);

      const queryString = queryParams.toString();
      return request(`/admin/users${queryString ? `?${queryString}` : ''}`);
    },

    // Enable user
    enableUser: (userId) => request(`/admin/users/${userId}/enable`, { method: 'POST' }),

    // Disable user
    disableUser: (userId) => request(`/admin/users/${userId}/disable`, { method: 'POST' }),

    // Get all documents
    getDocuments: (params = {}) => {
      const queryParts = [];
      if (params.limit) queryParts.push(`limit=${encodeURIComponent(params.limit)}`);
      if (params.offset) queryParts.push(`offset=${encodeURIComponent(params.offset)}`);
      if (params.title) queryParts.push(`title=${encodeURIComponent(params.title)}`);

      const queryString = queryParts.join('&');
      return request(`/admin/documents${queryString ? `?${queryString}` : ''}`);
    },

    // Get document detail
    getDocument: (documentId) => request(`/admin/documents/${documentId}`),

    // Get document Q&A (supports pagination and filtering)
    getDocumentQnA: (documentId, params = {}) => {
      const query = new URLSearchParams();
      if (params.qna_status) query.set('qna_status', params.qna_status);
      if (params.page_number != null) query.set('page_number', params.page_number);
      if (params.limit != null) query.set('limit', params.limit);
      if (params.offset != null) query.set('offset', params.offset);
      const qs = query.toString();
      return request(`/admin/documents/${documentId}/qna${qs ? '?' + qs : ''}`);
    },

    // Approve Q&A
    approveQnA: (documentId, qnaId) =>
      request(`/admin/documents/${documentId}/qna/${qnaId}/pass`, { method: 'POST' }),

    // Fail Q&A
    failQnA: (documentId, qnaId) =>
      request(`/admin/documents/${documentId}/qna/${qnaId}/fail`, { method: 'POST' }),

    // Ingest document (file upload)
    ingestDocument: async (formData) => {
      const token = await getValidIdToken();
      const response = await fetch(`${BASE_URL}/admin/documents/ingest`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to ingest document');
      }

      return response.json();
    },

    // Ingest video (YouTube URL)
    ingestVideo: async (data) => {
      const token = await getValidIdToken();
      const formData = new URLSearchParams();
      formData.append('title', data.title);
      formData.append('url', data.url);

      const response = await fetch(`${BASE_URL}/admin/documents/ingest/video`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to ingest video');
      }

      return response.json();
    },

    // Update document
    updateDocument: (documentId, data) =>
      request(`/admin/documents/${documentId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Delete document
    deleteDocument: (documentId) =>
      request(`/admin/documents/${documentId}`, { method: 'DELETE' }),

    // Get jobs list
    getJobs: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append('status', params.status);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);

      const queryString = queryParams.toString();
      return request(`/admin/documents/jobs${queryString ? `?${queryString}` : ''}`);
    },

    // Get job status
    getJobStatus: (jobId) => request(`/admin/documents/jobs/${jobId}`),

    // FAQ Management APIs
    // Get all FAQ entries
    getFAQs: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.category) queryParams.append('category', params.category);
      if (params.active !== undefined) queryParams.append('active', params.active);
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const queryString = queryParams.toString();
      return request(`/admin/faq${queryString ? `?${queryString}` : ''}`);
    },

    // Get single FAQ entry
    getFAQ: (faqId) => request(`/admin/faq/${faqId}`),

    // Create FAQ entry
    createFAQ: async (data) => {
      const token = await getValidIdToken();
      const formData = new URLSearchParams();
      formData.append('question', data.question);
      formData.append('answer', data.answer);
      if (data.category) formData.append('category', data.category);
      if (data.keywords) formData.append('keywords', data.keywords);
      if (data.qna_id) formData.append('qna_id', data.qna_id);

      const response = await fetch(`${BASE_URL}/admin/faq`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || error.message || 'Failed to create FAQ');
      }

      return response.json();
    },

    // Update FAQ entry
    updateFAQ: async (faqId, data) => {
      const token = await getValidIdToken();
      const formData = new URLSearchParams();
      if (data.question) formData.append('question', data.question);
      if (data.answer) formData.append('answer', data.answer);
      if (data.category !== undefined) formData.append('category', data.category);
      if (data.keywords !== undefined) formData.append('keywords', data.keywords);

      const response = await fetch(`${BASE_URL}/admin/faq/${faqId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || error.message || 'Failed to update FAQ');
      }

      return response.json();
    },

    // Delete FAQ entry
    deleteFAQ: (faqId) => request(`/admin/faq/${faqId}`, { method: 'DELETE' }),

    // Feedback Management APIs
    // List all feedback with optional filters
    getFeedbacks: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append('status', params.status);
      if (params.category) queryParams.append('category', params.category);
      if (params.user_id) queryParams.append('user_id', params.user_id);
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);

      const queryString = queryParams.toString();
      return request(`/admin/feedback${queryString ? `?${queryString}` : ''}`);
    },

    // Get feedback detail
    getFeedback: (feedbackUuid) => request(`/admin/feedback/${feedbackUuid}`),

    // Update feedback (status and/or priority)
    updateFeedback: (feedbackUuid, data) =>
      request(`/admin/feedback/${feedbackUuid}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    // Delete feedback
    deleteFeedback: (feedbackUuid) =>
      request(`/admin/feedback/${feedbackUuid}`, { method: 'DELETE' }),

    // Add admin comment to feedback
    addFeedbackComment: (feedbackUuid, comment) =>
      request(`/admin/feedback/${feedbackUuid}/comments`, {
        method: 'POST',
        body: JSON.stringify({ comment }),
      }),
  },
};
